import { buildApiUrl, buildAssetUrl } from "../../utils/api";

const normalizeProduct = (product) => {
  const startingPrice = Number.parseFloat(product.starting_price || 0);
  const imageUrls = [
    product.image1,
    product.image2,
    product.image3,
    product.image4,
    product.image5,
    product.image6
  ]
    .filter(Boolean)
    .map(buildAssetUrl);

  return {
    ...product,
    id: product.product_id,
    product_id: product.product_id,
    name: product.name ?? product.product_name ?? 'Product',
    category: product.category_name || '',
    subcategory: product.subcategory_name || '',
    image: imageUrls[0] || null,
    image2: imageUrls[1] || null,
    images: imageUrls,
    price: Number.isNaN(startingPrice) ? 0 : startingPrice,
    originalPrice: Number.isNaN(startingPrice) ? 0 : startingPrice,
    gst: Number.parseFloat(product.gst ?? product.gst_percentage ?? 0) || 0,
    discount: 0,
    unit: 'unit',
    inStock: true,
    rating: 0,
    created_at: product.created_at || new Date().toISOString(),
  };
};

const matchesSelectedCategory = (product, selectedCategory, categories) => {
  if (selectedCategory === 'all' || selectedCategory === null) {
    return true;
  }

  const normalizedSelection = String(selectedCategory).trim().toLowerCase();
  const matchedCategory = categories.find(
    (category) => String(category.category_id) === normalizedSelection
  );
  const selectedCategoryName = matchedCategory?.name?.trim().toLowerCase() || normalizedSelection;

  return product.category?.trim().toLowerCase() === selectedCategoryName;
};

export const createProductsSlice = (set, get) => ({
  products: [],
  products_loading: false,
  products_loaded: false,
  products_error: null,
  sort_by: 'relevant',
  min_price: 0,
  max_price: 10000,
  filters: {},

  fetchProducts: async () => {
    set({ products_loading: true, products_error: null });
    try {
      const response = await fetch(buildApiUrl("/api/v1/customer/products"));
      if (!response.ok) throw new Error('Failed to fetch products');

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch products');
      }

      const products = (data.data || []).map(normalizeProduct);
      const highestPrice = products.reduce((maxPrice, product) => (
        product.price > maxPrice ? product.price : maxPrice
      ), 0);

      set({
        products,
        products_loading: false,
        products_loaded: true,
        max_price: highestPrice || 10000,
      });

      return products;
    } catch (error) {
      set({ products_error: error.message, products_loading: false, products_loaded: true });
      console.error('Error fetching products:', error);
      return [];
    }
  },

  getFilteredProducts: () => {
    const { products, categories, selectedCategory, searchQuery, sort_by, min_price, max_price, filters } = get();
    let filtered = products;

    if (selectedCategory !== 'all' && selectedCategory !== null) {
      filtered = filtered.filter((product) => matchesSelectedCategory(product, selectedCategory, categories));
    }

    if (searchQuery?.trim()) {
      const terms = searchQuery.trim().toLowerCase().split(/\s+/);
      filtered = filtered.filter((product) => {
        const searchableText = [
          product.name,
          product.product_name,
          product.category,
          product.category_name,
          product.subcategory,
          product.subcategory_name,
          product.brand_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return terms.every((term) => searchableText.includes(term));
      });
    }

    filtered = filtered.filter(product => product.price >= min_price && product.price <= max_price);

    if (Object.keys(filters).length > 0) {
      filtered = filtered.filter(product => {
        return Object.keys(filters).every(key => {
          const filterValues = filters[key];
          return filterValues.includes(product[key]);
        });
      });
    }

    switch (sort_by) {
      case 'price_low_to_high':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price_high_to_low':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default:
        // relevant - keep original order
        break;
    }

    return filtered;
  },

  setSortBy: (sort) => set({ sort_by: sort }),
  setPriceRange: (min, max) => set({ min_price: min, max_price: max }),
  setFilters: (filters) => set({ filters }),
  resetFilters: () => set({
    sort_by: 'relevant',
    min_price: 0,
    max_price: 10000,
    filters: {}
  }),
});
